import { useEffect, useState } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useDispatch } from 'react-redux';

import { CustomAvatar, CustomAvatarGroup } from '../custom-avatar';
import {
  ITaskOverview,
  getTasksOverviewById,
  selectGetTaskOverviewByIdState,
} from '@app/modules/dashboard';
import { useRequestState } from '@app/hooks';
import { useAuthRole } from '@app/utils';
import { DataNotFound } from '../page-404';
import { selectMyProfileFormState } from '@app/modules/my-profile';
import { Strings } from '@app/constants';

interface Props {
  taskValue: any;
  title: string;
  teamMembers: any;
  color: any;
  heading?: string;
  resetSelectedMember?: boolean;
  setResetSelectedMember?: (value: boolean) => void;
  selectedProject?: number | null;
}

const TaskOverviewCard = ({
  taskValue,
  title,
  teamMembers,
  color,
  heading,
  resetSelectedMember,
  setResetSelectedMember,
  selectedProject,
}: Props) => {
  const theme = useTheme();
  const [selectedMember, setSelectedMember] = useState({ id: null, memeberName: '' });
  const dispatch = useDispatch();
  const { isManager } = useAuthRole();

  const { data: profileData } = useRequestState({
    stateSelector: selectMyProfileFormState,
    errorShown: true,
  });

  const handleClick = (id: any, name: string) => () => {
    if (selectedMember?.id === id) {
      setSelectedMember({ id: null, memeberName: '' });
    } else {
      setSelectedMember({ id, memeberName: name });
      dispatch(getTasksOverviewById({ projectId: selectedProject, createdBy: id }));
    }
  };

  useEffect(() => {
    if (selectedProject) {
      dispatch(getTasksOverviewById({ projectId: selectedProject, createdBy: profileData?.id }));
      setSelectedMember({ id: null, memeberName: '' });
    }
  }, [selectedProject, dispatch, profileData?.id]);

  const { data } = useRequestState({
    stateSelector: selectGetTaskOverviewByIdState,
    errorShown: true,
  });

  const getTaskCountByListName = (listName: string) =>
    taskValue?.lists
      ?.filter((list: ITaskOverview) => list.listName === listName)
      .reduce((acc: number, list: ITaskOverview) => acc + list.listTasks, 0);

  const getTaskCountByListNameMember = (listName: string) =>
    data?.lists
      ?.filter((list: ITaskOverview) => list.listName === listName)
      .reduce((acc: number, list: ITaskOverview) => acc + list.listTasks, 0);

  const taskListData = taskValue?.lists?.map((list: ITaskOverview, index: number) => ({
    listName: list.listName,
    taskCount: getTaskCountByListName(list.listName),
    color: color[index].color,
    typographyColor: color[index].typographyColor,
  }));

  const taskListDataMember = data?.lists?.map((list: ITaskOverview, index: number) => ({
    listName: list.listName,
    taskCount: getTaskCountByListNameMember(list.listName),
    color: color[index].color,
    typographyColor: color[index].typographyColor,
  }));

  const formattedData = selectedMember?.id ? taskListDataMember : taskListData;

  const maxTaskCount = Math.max(
    ...(formattedData?.map((item: any) => item.taskCount) || [1]),
    selectedMember?.id ? data?.totalTasks : taskValue.totalTasks
  );

  return (
    <Stack height="100%">
      <Stack pl={2} mt={1.5}>
        <Typography variant="h6">{title}</Typography>
      </Stack>
      {data ? (
        <>
          {heading === 'manager' && (
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              mx={2}
              mt={1}
            >
              <Stack sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} gap={1}>
                {selectedMember?.id && (
                  <CustomAvatar name={selectedMember?.memeberName} color="info" />
                )}
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: theme.palette.grey[600],
                  }}
                >
                  {selectedMember?.memeberName}
                </Typography>
              </Stack>
              {isManager ? (
                <CustomAvatarGroup max={10} size="small">
                  {teamMembers?.length > 0 &&
                    teamMembers?.map((team: ITaskOverview, index: number) => {
                      if (team.firstName) {
                        return (
                          <CustomAvatar
                            style={{
                              boxShadow:
                                selectedMember?.id === team.id
                                  ? `0 0 0 2px ${theme.palette.primary.main}`
                                  : 'none',
                            }}
                            key={index}
                            name={`${team.firstName} ${team.lastName}`}
                            onClick={handleClick(team.id, `${team.firstName} ${team.lastName}`)}
                            color="info"
                          />
                        );
                      }
                      return null;
                    })}
                </CustomAvatarGroup>
              ) : null}
            </Stack>
          )}

          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
            gap={2}
            px={2}
            pt={3}
          >
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 0.6,
              }}
            >
              <Stack
                sx={{
                  height: 20,
                  width: 20,
                  backgroundColor: theme.palette.quaternary.lightGreen,
                  borderRadius: 0.5,
                }}
              />
              <Typography sx={{ fontSize: '14px', fontWeight: 550 }}>
                {Strings.tasks.totalTask}
              </Typography>
            </Stack>

            {formattedData?.map((data: any, index: any) => (
              <Stack
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: 0.6,
                }}
              >
                <Stack
                  sx={{
                    height: 20,
                    width: 20,
                    backgroundColor: data.color,
                    borderRadius: 0.5,
                  }}
                />
                <Typography sx={{ fontSize: '14px', fontWeight: 550 }}>{data.listName}</Typography>
              </Stack>
            ))}
          </Stack>
          <Stack
            sx={{
              display: 'flex',
              gap: { md: 2.5, xs: 1 },
              p: 2,
              flexDirection: 'row',
              justifyItems: 'center',
              height: '70%',
              width: '100%',
              position: 'relative',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: { md: 60, xs: 200 },
                height: '100%',
                position: 'relative',
                alignSelf: 'center',
                borderRadius: '6px',
                justifyItems: 'center',
                backgroundColor: theme.palette.grey[200],
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Stack
                sx={{
                  height: `${
                    ((selectedMember?.id ? data?.totalTasks : taskValue.totalTasks) /
                      maxTaskCount) *
                    100
                  }%`,
                  width: '100%',
                  borderRadius: '6px',
                  backgroundColor: theme.palette.quaternary.lightGreen,
                  bottom: 0,
                  left: 0,
                  position: 'absolute',
                }}
              >
                <Typography
                  sx={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: theme.palette.success.darker,
                    fontWeight: 'bold',
                  }}
                >
                  {selectedMember?.id ? data?.totalTasks : taskValue.totalTasks}
                </Typography>
              </Stack>
            </Box>
            {formattedData?.map(
              (
                {
                  taskCount: val,
                  color,
                  typographyColor,
                }: {
                  taskCount: number;
                  color: string | undefined;
                  typographyColor: string | undefined;
                },
                index: number
              ) => (
                <Box
                  key={index}
                  sx={{
                    width: { md: 60, xs: 200 },
                    height: '100%',
                    position: 'relative',
                    alignSelf: 'center',
                    borderRadius: '6px',
                    justifyItems: 'center',
                    backgroundColor: theme.palette.grey[200],
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <Stack
                    sx={{
                      height: `${(val / maxTaskCount) * 100}%`,
                      width: '100%',
                      borderRadius: '6px',
                      backgroundColor: color,
                      bottom: 0,
                      left: 0,
                      position: 'absolute',
                    }}
                  >
                    <Typography
                      sx={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: typographyColor,
                        fontWeight: 'bold',
                      }}
                    >
                      {val}
                    </Typography>
                  </Stack>
                </Box>
              )
            )}
          </Stack>
          <Stack
            textAlign={'center'}
            color={theme.palette.quaternary.bottleGreen}
            fontSize={20}
            fontWeight={600}
          >
            {taskValue?.projectName}
          </Stack>
        </>
      ) : (
        <Stack sx={{ height: '100%', justifyContent: 'center' }}>
          <DataNotFound height={200} />
        </Stack>
      )}
    </Stack>
  );
};

export { TaskOverviewCard };
